package com.iregained.site.components.sections.testimonials.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.testimonials.styles.TestimonialCardStyle
import com.iregained.site.components.sections.testimonials.styles.TestimonialFilterButtonActiveStyle
import com.iregained.site.components.sections.testimonials.styles.TestimonialFilterButtonStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.components.widgets.VideoFormat
import com.iregained.site.components.widgets.YouTubeVideoCard
import com.iregained.site.theme.SitePalette
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

// --- UNIQUE SECTION ANIMATION ---
@Composable
private fun Modifier.gentleExpand(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.95f)
        .translateY(if (isVisible) 0.px else 20.px).transition(
            Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun PatientsRecoveryStoriesSection(data: PatientsRecoveryStoriesData = defaultPatientsRecoveryStoriesData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-recovery-stories"
    var isVisible by remember { mutableStateOf(false) }

    var selectedCategory by remember { mutableStateOf(TestimonialCategory.ALL) }

    val filteredTestimonials = remember(selectedCategory, data.testimonials) {
        if (selectedCategory == TestimonialCategory.ALL) data.testimonials
        else data.testimonials.filter { it.category == selectedCategory }
    }

    if (data.testimonials.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1200.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.headline.isNotBlank()) {
                        SectionHeader(
                            text = data.headline,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 32.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    // --- FILTER BAR ---
                    Row(
                        modifier = Modifier.fillMaxWidth().margin(bottom = 48.px).display(DisplayStyle.Flex)
                            .flexWrap(FlexWrap.Wrap).justifyContent(JustifyContent.Center).gap(16.px)
                            .opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s, delay = 150.ms))
                    ) {
                        TestimonialCategory.entries.forEach { category ->
                            key(category.name) {
                                val isActive = selectedCategory == category
                                val buttonStyle = if (isActive) TestimonialFilterButtonStyle.toModifier()
                                    .then(TestimonialFilterButtonActiveStyle.toModifier()) else TestimonialFilterButtonStyle.toModifier()

                                Button(attrs = buttonStyle.onClick { selectedCategory = category }.toAttrs()) {
                                    Text(category.displayName)
                                }
                            }
                        }
                    }

                    // --- TESTIMONIAL GRID ---
                    SimpleGrid(
                        numColumns = numColumns(base = 1, md = 2, lg = 3), modifier = Modifier.fillMaxWidth().gap(32.px)
                    ) {
                        filteredTestimonials.forEachIndexed { index, testimonial ->
                            key(testimonial.quote.take(20)) {
                                TestimonialCard(
                                    testimonial, palette, Modifier.gentleExpand(isVisible, 200 + (index * 100))
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TestimonialCard(item: TestimonialItem, palette: SitePalette, modifier: Modifier) {
    // Add local state to track hover for inner element animations
    var isHovered by remember { mutableStateOf(false) }

    Box(modifier = TestimonialCardStyle.toModifier().onMouseEnter { isHovered = true }
        .onMouseLeave { isHovered = false }.then(modifier)) {
        Column(modifier = Modifier.fillMaxSize()) {

            // 1. Check if it is a Video Testimonial
            if (!item.embedUrl.isNullOrBlank()) {
                Box(
                    modifier = Modifier.fillMaxWidth().margin(bottom = 20.px).borderRadius(8.px)
                        .overflow(Overflow.Hidden)
                ) {
                    YouTubeVideoCard(
                        embedUrl = item.embedUrl,
                        thumbnailUrl = item.thumbnailUrl,
                        ariaLabel = "Watch testimonial from ${item.authorName}",
                        labelPillText = "Watch",
                        videoFormat = VideoFormat.STANDARD_16_9,
                        isCompact = true,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            } else {
                // 2. Text Only Testimonial: Render a large stylistic Quote Mark with animation
                Div(
                    attrs = Modifier.fontSize(64.px).lineHeight(0.5).fontWeight(FontWeight.Bold)
                        .color(if (isHovered) palette.brand.primary else palette.brand.primarySoft)
                        .margin(bottom = 16.px).rotate(if (isHovered) (-10).deg else 0.deg).transition(
                            Transition.of("color", 0.3.s, TransitionTimingFunction.EaseOut),
                            Transition.of("transform", 0.3.s, TransitionTimingFunction.EaseOut)
                        ).toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = "“"
                                onDispose {}
                            }
                        })
            }

            // Quote Text (Expands to push author down)
            Div(
                attrs = Modifier.fontSize(16.px).color(palette.custom.darkText).lineHeight(1.6).margin(bottom = 24.px)
                    .flexGrow(1).toAttrs {
                        ref { htmlElement ->
                            htmlElement.innerHTML = if (item.embedUrl.isNullOrBlank()) item.quote else "“${item.quote}”"
                            onDispose {}
                        }
                    })

            // Author Section
            Column(
                modifier = Modifier.fillMaxWidth().borderTop(1.px, LineStyle.Solid, Color.rgb(0xEEEEEE))
                    .padding(top = 16.px)
            ) {
                Div(
                    attrs = Modifier.fontSize(16.px).color(palette.brand.primary).fontWeight(FontWeight.Bold).toAttrs {
                        ref { htmlElement ->
                            htmlElement.innerHTML = item.authorName
                            onDispose {}
                        }
                    })
                if (!item.authorTitle.isNullOrBlank()) {
                    Div(
                        attrs = Modifier.fontSize(14.px).color(Colors.Gray).margin(top = 4.px).toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = item.authorTitle
                                onDispose {}
                            }
                        })
                }
            }
        }
    }
}

// ==========================================
// DATA MODELS
// ==========================================

enum class TestimonialCategory(val displayName: String) {
    ALL("All Stories"), PATIENT("Patient Recovery"), CLINICIAN("Clinical Insights")
}

data class TestimonialItem(
    val category: TestimonialCategory,
    val authorName: String,
    val authorTitle: String? = null,
    val quote: String,
    val embedUrl: String? = null,
    val thumbnailUrl: String? = null
)

data class PatientsRecoveryStoriesData(
    val sectionId: String? = null, val headline: String, val testimonials: List<TestimonialItem>
)

val defaultPatientsRecoveryStoriesData = PatientsRecoveryStoriesData(
    sectionId = "patients-recovery-stories", headline = "STORIES OF RECOVERY & CLINICAL SUCCESS", testimonials = listOf(
        // ==========================================
        // VIDEOS (PATIENTS)
        // ==========================================
        TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Beverly Charles",
            authorTitle = "Stroke Survivor",
            quote = "Hand movements after four years of stroke.",
            embedUrl = Res.Strings.Links.TESTIMONIAL_BEVERLEY,
            thumbnailUrl = Res.Images.Home.Testimonials.THUMB_BEVERLY
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "David A. Leblanc",
            authorTitle = "Stroke Survivor",
            quote = "I can use a nail clipper.",
            embedUrl = Res.Strings.Links.TESTIMONIAL_DAVID,
            thumbnailUrl = Res.Images.Home.Testimonials.THUMB_DAVID
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Dr. Michael Franklyn",
            authorTitle = "Stroke Survivor",
            quote = "Saved by The MyHand<sup>®</sup> System.",
            embedUrl = Res.Strings.Links.TESTIMONIAL_MICHAEL,
            thumbnailUrl = Res.Images.Home.Testimonials.THUMB_MIKE
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Dr. Byron Eastman",
            authorTitle = "Stroke Survivor",
            quote = "Picked up photography again after a long hiatus.",
            embedUrl = Res.Strings.Links.TESTIMONIAL_BYRON,
            thumbnailUrl = Res.Images.Home.Testimonials.THUMB_BYRON
        ),

        // ==========================================
        // WRITTEN QUOTES (CLINICIANS)
        // ==========================================
        TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Deb",
            authorTitle = "OT, Frazier Neuro-rehab Institute (KY, USA)",
            quote = "What my patient achieved in 5 sessions using the MyHand<sup>®</sup> System... I couldn't have achieved in 6 months of therapy using other conventional therapies, even though we have some of the state-of-the-art devices here at our facility."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Shelly",
            authorTitle = "OT, Frazier Neuro-rehab Institute (KY, USA)",
            quote = "My pediatric patient with chronic hemiparesis had 2 sessions with immediate gains and increased scoring on target activity. He is a gamer and really responded well to the format."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Eliza",
            authorTitle = "OT, Frazier Neuro-rehab Institute (KY, USA)",
            quote = "This modality has been great for spasticity management... In just three sessions he has made more progress through his hand than we made during his initial 7."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Ellen",
            authorTitle = "OT, University Hospitals Cleveland (OH, USA)",
            quote = "I have been utilizing the MyHand<sup>®</sup> more in my treatments. I have seen positive engagement and results, and the patients all seem to be pleased and excited."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Pam",
            authorTitle = "Rehab Director, University Hospitals Cleveland (OH, USA)",
            quote = "Our OTs are very happy with the machine and would like to continue to use it with our patients."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Mona Kalra",
            authorTitle = "PT, Maplecare Physiotherapy (Ottawa, Canada)",
            quote = "Our experience so far has reinforced that MyHand<sup>®</sup> is a powerful tool in neuro-rehabilitation, particularly in bridging the gap between early movement return and functional hand use."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Dr. Subramanian",
            authorTitle = "University of Texas Health Sciences Centre (TX, USA)",
            quote = "Patients who have been using the device in the lab have been impressed at the level of hand and finger movement they have achieved. We see improvements in a variety of symptoms along the upper limb."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Occupational Therapist",
            authorTitle = "Phoenix Area, USA",
            quote = "I've never seen a device as unique and well equipped as the MyHand<sup>®</sup> to treat the hand."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Occupational Therapist",
            authorTitle = "Fort Wayne, USA",
            quote = "I see that as something I can use with a lot of patients who have lost hope for their hand. I can't wait to start using it."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Occupational Therapist",
            authorTitle = "Louisville, KY, USA",
            quote = "With my patient with chronic spasticity and poor posture, the screen location and meaningful activities kept his head lifted and in position for a much longer period of time."
        ), TestimonialItem(
            category = TestimonialCategory.CLINICIAN,
            authorName = "Occupational Therapist",
            authorTitle = "Louisville, KY, USA",
            quote = "I have a patient with severe aphasia and ideomotor apraxia. With 3 sessions using IRegained, he demonstrates increased understanding of task demands attributed to the biofeedback component. He is now able to isolate digit flexion despite severe spasticity."
        ),

        // ==========================================
        // WRITTEN QUOTES (PATIENTS)
        // ==========================================
        TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Frank",
            authorTitle = "Patient, Frazier Rehab Institute (KY, USA)",
            quote = "The amount of finger and hand function I attained using the MyHand<sup>®</sup> System over the last 2-3 weeks has been phenomenal. I am now using my hands in my daily life—including using the shampoo bottle, buttoning my shirt, and zipping my pants."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Dr. J",
            authorTitle = "Orthopedic Surgeon & Patient (OH, USA)",
            quote = "I had my stroke 3 years ago and tried multiple therapies to no avail. Using the MyHand<sup>®</sup> System, my fingers move much better, swelling has reduced, and I am now able to use my fingers for small functional tasks."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Mr. G",
            authorTitle = "Patient, UT San Antonio Health (TX, USA)",
            quote = "After only 3 sessions using the MyHand<sup>®</sup> System, my fingers have started to move more, swelling has reduced substantially, and my spasticity has decreased. I am looking forward to regaining my hand function."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Chris",
            authorTitle = "Patient (Thunderbay, Canada)",
            quote = "My gut feeling is you should have the MyHand<sup>®</sup> System right away in the hospital, right beside your bed."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Diana",
            authorTitle = "Patient (Sudbury, Canada)",
            quote = "The more your hand functions, the more impact it has on your life."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Jacquiline",
            authorTitle = "Patient (Thunderbay, Canada)",
            quote = "I carry my yogurt; my little cup and I can hold it and it doesn't go flying all over the place."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "David",
            authorTitle = "Patient (Sudbury, Canada)",
            quote = "I can wash my cups in the sink and press... before I just held onto the handle with my non-affected hand. Now it's really good."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Susan",
            authorTitle = "Patient (Louisville, KY, USA)",
            quote = "I have had my hand curled up for 23 years since I had my stroke and for the first time my hand opened up and my fingers moved on purpose...!"
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "Kathy",
            authorTitle = "Patient (Louisville, KY, USA)",
            quote = "Having been in the medical field, I accessed all resources and didn't see a change in 22 years. Using the device for just 30 minutes got my hand completely relaxed."
        ), TestimonialItem(
            category = TestimonialCategory.PATIENT,
            authorName = "June",
            authorTitle = "Patient (Louisville, KY, USA)",
            quote = "This is fantastic. Huge contribution to patients with disability. I can't believe my fingers are actually moving..."
        )
    )
)